import { captureException, withScope } from "@sentry/react";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
  MutationCache,
  onlineManager,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import type { PersistRetryer } from "@tanstack/react-query-persist-client";
import {
  PersistQueryClientProvider,
  removeOldestQuery,
} from "@tanstack/react-query-persist-client";
import { hoursToMilliseconds } from "date-fns/hoursToMilliseconds";
import { del, get, set } from "idb-keyval";
import { type PropsWithChildren, Suspense, useMemo } from "react";
import { toolKeys } from "~api/toolKeys.ts";
import { UploadQueueVisibilityProvider } from "~components/Resources/PhotoDocumentation/UploadQueueVisibilityContext.tsx";
import { updateTool } from "~components/Resources/ToolAdministration/api/toolAdministration.ts";
import { createTimeTrackingEventsMutationKey } from "~components/TimeManagement/api/createTimeTrackingEventsMutationKey.ts";
import { timeEntriesKeys } from "~components/TimeManagement/api/timeEntries.ts";
import { timeTrackingKeys } from "~components/TimeManagement/api/timeTracking.ts";
import { createTimeTrackingEvents } from "~components/TimeManagement/api/timeTrackingEvent.ts";
import { ApiError } from "~generated";
import { isTransientTokenError } from "../auth/isTransientTokenError.ts";
import { TokenUnavailableError } from "../auth/TokenUnavailableError.ts";
import { requestTokenRefresh } from "../auth/useAccessToken.ts";
import { handleError } from "../lib/handleError.ts";
import { isBenignNetworkError } from "../lib/isBenignNetworkError.ts";
import { isRecoverableStorageError } from "../lib/isRecoverableStorageError.ts";
import { lazyWithRetry } from "../lib/lazyWithRetry.ts";
import { config } from "./config.ts";

const MAX_QUERY_RETRIES = 3;

// Either already reported where they were raised (fetchToken) or transient client-side conditions
export function isUnreportableError(error: unknown): boolean {
  return (
    isBenignNetworkError(error) ||
    isTransientTokenError(error) ||
    error instanceof TokenUnavailableError
  );
}

export function isExpectedMutationError(
  error: unknown,
  meta: Record<string, unknown> | undefined,
): boolean {
  const expected = meta?.expectedErrorStatuses;
  return (
    error instanceof ApiError &&
    Array.isArray(expected) &&
    expected.includes(error.status)
  );
}

// Every ApiError is thrown from the same frame of the generated client, so Sentry
// would otherwise group a 500 on one endpoint with a 401 on another into one issue
export function apiErrorFingerprint(error: ApiError): string[] {
  return [
    "ApiError",
    String(error.status),
    error.request.method,
    error.request.url,
  ];
}

export function retryQueryWithTokenRefresh(
  failureCount: number,
  error: unknown,
): boolean {
  if (error instanceof ApiError && error.status === 401) {
    // the server rejected a token msal still considers valid; the retry then fetches a fresh one
    requestTokenRefresh();
  }
  return failureCount < MAX_QUERY_RETRIES;
}

export const retryPersistWithoutOldestQuery: PersistRetryer = (props) => {
  if (isRecoverableStorageError(props.error)) {
    return undefined;
  }
  handleError(props.error);
  return removeOldestQuery(props);
};

function useInitQueryClient() {
  return useMemo(() => {
    const queryClient = new QueryClient({
      // see https://aronschueler.de/blog/2022/12/16/generating-meaningful-issues-in-sentry-with-react-query-+-axios/
      // for error handling
      queryCache: new QueryCache({
        onError: (err, query) => {
          if (isUnreportableError(err)) {
            return;
          }

          withScope((scope) => {
            if (err instanceof ApiError) {
              scope.setFingerprint(apiErrorFingerprint(err));
            }
            scope.setContext("query", { queryHash: query.queryHash });
            captureException(err);
          });
        },
      }),
      mutationCache: new MutationCache({
        onError: (err, _variables, _context, mutation) => {
          if (err instanceof ApiError && err.status === 422) {
            // 422 is a validation error, so we don't need to capture it
            return;
          }

          if (
            isUnreportableError(err) ||
            isExpectedMutationError(err, mutation.meta)
          ) {
            return;
          }

          withScope((scope) => {
            if (err instanceof ApiError) {
              scope.setFingerprint(apiErrorFingerprint(err));
            }
            scope.setContext("mutation", {
              mutationId: mutation.mutationId,
              variables: mutation.state.variables,
            });
            captureException(err);
          });
        },
      }),
      defaultOptions: {
        queries: {
          networkMode: "online",
          refetchOnWindowFocus: false,
          gcTime: hoursToMilliseconds(24),
          retry: retryQueryWithTokenRefresh,
        },
        mutations: {
          networkMode: "online",
          gcTime: hoursToMilliseconds(24) * 7,
        },
      },
    });

    queryClient.setMutationDefaults(toolKeys.updateToolMutationKey, {
      mutationFn: updateTool,
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
      },
    });
    queryClient.setMutationDefaults(createTimeTrackingEventsMutationKey, {
      mutationFn: createTimeTrackingEvents,
      onSettled: async (_data, _error, { tenantId }) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: timeTrackingKeys.base(tenantId),
          }),
          queryClient.invalidateQueries({ queryKey: timeEntriesKeys.base() }),
        ]);
      },
    });

    // onlineManager assumes online until an online/offline event fires, which never happens when opening the page offline
    onlineManager.setOnline(navigator.onLine !== false);

    if (!onlineManager.isOnline()) {
      // when offline while opening the page, queryClient.resumePausedMutations()
      // does not work reliably, so we wait to be online and then resume all paused
      // mutations once.
      const unsubscribe = onlineManager.subscribe(async () => {
        if (onlineManager.isOnline()) {
          const mutations = queryClient
            .getMutationCache()
            .getAll()
            .filter((m) => m.state.isPaused);

          await Promise.all(mutations.map((m) => m.continue().catch(() => {})));
          await queryClient.invalidateQueries();
          unsubscribe();
        }
      });
    }

    return queryClient;
  }, []);
}

const asyncStorage = {
  getItem: (key: string) => get(key),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
};

const persister = createAsyncStoragePersister({
  storage: asyncStorage,
  retry: retryPersistWithoutOldestQuery,
});

// Pulls in dexie; nothing on the first paint needs it
const PhotoDocUploadQueueRunner = lazyWithRetry(() =>
  import(
    "~components/Resources/PhotoDocumentation/PhotoUpload/PhotoDocUploadQueueRunner.tsx"
  ).then((m) => ({ default: m.PhotoDocUploadQueueRunner })),
);

export function InitializeReactQuery({ children }: PropsWithChildren) {
  const queryClient = useInitQueryClient();

  if (config().disablePersistQueryCache === "true") {
    return (
      <UploadQueueVisibilityProvider>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={null}>
            <PhotoDocUploadQueueRunner />
          </Suspense>
          {children}
        </QueryClientProvider>
      </UploadQueueVisibilityProvider>
    );
  }

  return (
    <UploadQueueVisibilityProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
        // onSuccess is called twice due to react-strict mode
        // so we end up with duplicate mutations in development.
        onSuccess={async () => {
          await queryClient.resumePausedMutations();
          await queryClient.invalidateQueries();
        }}
      >
        <Suspense fallback={null}>
          <PhotoDocUploadQueueRunner />
        </Suspense>
        {children}
      </PersistQueryClientProvider>
    </UploadQueueVisibilityProvider>
  );
}
